import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Breadcrumbs, Link, Stack, Typography, SxProps, Theme } from '@mui/material';
import { tokens } from '../../theme';

export interface Crumb {
  label: string;
  to?: string;
}

interface PageHeaderProps {
  title: React.ReactNode;
  /** Jedno zdanie pod tytulem: czego dotyczy strona albo stan (np. „3 pracowników w zespole”). */
  description?: React.ReactNode;
  /** Mala linia nad tytulem, gdy nie ma sciezki — np. data na Panelu. */
  eyebrow?: React.ReactNode;
  /** Sciezka nad tytulem. Ostatni element to zwykle obszar menu, bez linku. */
  breadcrumbs?: Crumb[];
  /** Przyciski po prawej. Najwyzej jeden przycisk glowny (contained). */
  actions?: React.ReactNode;
  /** Tresc pod naglowkiem w tym samym bloku, np. zakladki. */
  children?: React.ReactNode;
  sx?: SxProps<Theme>;
}

/**
 * Naglowek kazdej strony panelu: sciezka, szeryfowy tytul, opis i akcje.
 * Ten sam uklad na kazdym ekranie — uzytkownik zawsze wie, gdzie jest.
 */
export const PageHeader: React.FC<PageHeaderProps> = ({ title, description, eyebrow, breadcrumbs, actions, children, sx }) => (
  <Box component="header" sx={{ mb: { xs: 2.5, md: 3.5 }, ...((sx as object) || {}) }}>
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      alignItems={{ xs: 'stretch', sm: 'flex-end' }}
      justifyContent="space-between"
      gap={{ xs: 2, sm: 3 }}
    >
      <Box sx={{ minWidth: 0 }}>
        {breadcrumbs && breadcrumbs.length > 0 ? (
          <Breadcrumbs aria-label="Ścieżka" sx={{ mb: 0.75, '& .MuiBreadcrumbs-li': { fontWeight: 600 } }}>
            {breadcrumbs.map((c, i) =>
              c.to ? (
                <Link key={`${c.label}-${i}`} component={RouterLink} to={c.to} underline="hover" sx={{ color: tokens.muted, fontWeight: 600, fontSize: '0.8125rem' }}>
                  {c.label}
                </Link>
              ) : (
                <Typography key={`${c.label}-${i}`} sx={{ color: tokens.muted, fontWeight: 600, fontSize: '0.8125rem' }}>
                  {c.label}
                </Typography>
              ),
            )}
          </Breadcrumbs>
        ) : eyebrow ? (
          <Typography sx={{ color: tokens.muted, fontWeight: 600, fontSize: '0.8125rem', mb: 0.75, '&::first-letter': { textTransform: 'uppercase' } }}>
            {eyebrow}
          </Typography>
        ) : null}
        <Typography variant="h1" component="h1" sx={{ textWrap: 'balance', wordBreak: 'break-word' } as object}>
          {title}
        </Typography>
        {description && (
          <Typography component="div" sx={{ mt: 0.75, color: tokens.inkSoft, fontSize: '0.9375rem', maxWidth: '72ch' }}>
            {description}
          </Typography>
        )}
      </Box>
      {actions && (
        <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center" justifyContent={{ xs: 'flex-start', sm: 'flex-end' }} sx={{ flexShrink: 0 }}>
          {actions}
        </Stack>
      )}
    </Stack>
    {children && <Box sx={{ mt: 2.5 }}>{children}</Box>}
  </Box>
);

export default PageHeader;
